import React, { useState, useEffect } from 'react';
import { UserService } from '@/services/userService';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Pagination from '@/components/ui/Pagination';
import toast from 'react-hot-toast';

interface User {
  _id: string;
  name: string;
  username: string;
  email: string;
  role: 'user' | 'admin';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CreateUserData {
  name: string;
  username: string;
  email: string;
  password: string;
  role?: 'user' | 'admin';
}

interface UpdateUserData {
  name?: string;
  email?: string;
  username?: string;
  role?: 'user' | 'admin';
  isActive?: boolean;
}

interface UserStats {
  total: number;
  active: number;
  inactive: number;
  admins: number;
  regularUsers: number;
  recentUsers: number;
}

interface UserFormData {
  name: string;
  username: string;
  email: string;
  password: string;
  role: 'user' | 'admin';
}

const INITIAL_FORM_DATA: UserFormData = {
  name: '',
  username: '',
  email: '',
  password: '',
  role: 'user'
};

export const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalUsers: 0,
    hasNext: false,
    hasPrev: false
  });

  // Filtri
  const [filters, setFilters] = useState({
    search: '',
    role: 'all' as 'all' | 'user' | 'admin',
    status: 'all' as 'all' | 'active' | 'inactive'
  });

  const [isFiltering, setIsFiltering] = useState(false);

  // Hook per debounce dei filtri
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  // Debounce filters per evitare troppe chiamate API
  useEffect(() => {
    // Se i filtri sono diversi da quelli debounced, mostra indicatore di caricamento
    const hasChanges = JSON.stringify(filters) !== JSON.stringify(debouncedFilters);
    setIsFiltering(hasChanges);

    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 500); // 500ms di ritardo

    return () => clearTimeout(timer);
  }, [filters, debouncedFilters]);

  // Modali
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Form data
  const [formData, setFormData] = useState<UserFormData>(INITIAL_FORM_DATA);
  const [editFormData, setEditFormData] = useState<Partial<UserFormData>>({});
  const [newPassword, setNewPassword] = useState('');

  // Carica utenti e statistiche solo quando cambiano i filtri debounced o la pagina
  const loadUsersAndStats = async () => {
    try {
      setLoading(true);

      const queryParams = {
        page: pagination.currentPage,
        limit: 10,
        ...(debouncedFilters.search && { search: debouncedFilters.search }),
        ...(debouncedFilters.role !== 'all' && { role: debouncedFilters.role as 'user' | 'admin' }),
        ...(debouncedFilters.status !== 'all' && { isActive: debouncedFilters.status === 'active' })
      };

      const [usersData, statsData] = await Promise.all([
        UserService.getAllUsers(queryParams),
        UserService.getUserStats()
      ]);

      setUsers(usersData.users);
      setPagination(usersData.pagination);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Errore nel caricamento degli utenti');
    } finally {
      setLoading(false);
      setIsFiltering(false);
    }
  };

  useEffect(() => {
    loadUsersAndStats();
  }, [pagination.currentPage, debouncedFilters]);

  // Gestori per filtri
  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      role: 'all',
      status: 'all'
    });
  };

  // Gestori per i modali
  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setActionLoading('create');

      const userData: CreateUserData = {
        name: formData.name,
        username: formData.username,
        email: formData.email,
        password: formData.password,
        role: formData.role
      };

      await UserService.createUser(userData);
      toast.success('Utente creato con successo');
      setShowCreateModal(false);
      setFormData(INITIAL_FORM_DATA);
      loadUsersAndStats();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Errore nella creazione dell\'utente');
    } finally {
      setActionLoading(null);
    }
  };

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    try {
      setActionLoading('edit');

      const updateData: UpdateUserData = {};
      if (editFormData.name) updateData.name = editFormData.name;
      if (editFormData.username) updateData.username = editFormData.username;
      if (editFormData.email) updateData.email = editFormData.email;
      if (editFormData.role) updateData.role = editFormData.role;

      await UserService.updateUser(selectedUser._id, updateData);
      toast.success('Utente aggiornato con successo');
      setShowEditModal(false);
      setEditFormData({});
      setSelectedUser(null);
      loadUsersAndStats();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Errore nell\'aggiornamento dell\'utente');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser || !newPassword) return;

    try {
      setActionLoading('password');

      await UserService.updateUserPassword(selectedUser._id, newPassword);
      toast.success('Password aggiornata con successo');
      setShowPasswordModal(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (error) {
      console.error('Error updating password:', error);
      toast.error('Errore nell\'aggiornamento della password');
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleActive = async (user: User) => {
    try {
      setActionLoading(`toggle-${user._id}`);

      const result = await UserService.toggleUserActive(user._id);
      toast.success(result.message);
      loadUsersAndStats();
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast.error('Errore nel cambio stato dell\'utente');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteUser = async (user: User) => {
    if (!confirm(`Sei sicuro di voler eliminare l'utente ${user.name}? Questa azione non può essere annullata.`)) {
      return;
    }

    try {
      setActionLoading(`delete-${user._id}`);

      const result = await UserService.deleteUser(user._id);
      toast.success(result.message);
      loadUsersAndStats();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Errore nell\'eliminazione dell\'utente');
    } finally {
      setActionLoading(null);
    }
  };

  // Gestori per i modali di modifica
  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setEditFormData({
      name: user.name,
      username: user.username,
      email: user.email,
      role: user.role
    });
    setShowEditModal(true);
  };

  const openPasswordModal = (user: User) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header con statistiche */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Gestione Utenti</h1>
              <p className="text-gray-600">Visualizza e gestisci tutti gli utenti</p>
            </div>

            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Nuovo Utente
            </button>
          </div>

          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
                <div className="text-sm text-blue-800">Totale</div>
              </div>
              <div className="bg-green-50 p-3 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{stats.active}</div>
                <div className="text-sm text-green-800">Attivi</div>
              </div>
              <div className="bg-red-50 p-3 rounded-lg">
                <div className="text-2xl font-bold text-red-600">{stats.inactive}</div>
                <div className="text-sm text-red-800">Inattivi</div>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">{stats.admins}</div>
                <div className="text-sm text-purple-800">Admin</div>
              </div>
              <div className="bg-yellow-50 p-3 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">{stats.regularUsers}</div>
                <div className="text-sm text-yellow-800">Utenti</div>
              </div>
              <div className="bg-indigo-50 p-3 rounded-lg">
                <div className="text-2xl font-bold text-indigo-600">{stats.recentUsers}</div>
                <div className="text-sm text-indigo-800">Recenti</div>
              </div>
            </div>
          )}
        </div>

        {/* Sezione Filtri */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-gray-900">Filtri</h2>
              {isFiltering && (
                <div className="flex items-center gap-1 text-blue-600 text-sm">
                  <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                  Filtraggio...
                </div>
              )}
            </div>
            <button
              onClick={clearFilters}
              className="text-gray-500 hover:text-gray-700 text-sm"
            >
              Pulisci tutti
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ricerca</label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Nome o email..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ruolo</label>
              <select
                value={filters.role}
                onChange={(e) => handleFilterChange('role', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Tutti i ruoli</option>
                <option value="user">Utenti</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stato</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Tutti gli stati</option>
                <option value="active">Attivi</option>
                <option value="inactive">Inattivi</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tabella utenti */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ruolo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stato
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Registrato
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.username}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user.role === 'admin'
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-blue-100 text-blue-800'
                        }`}>
                        {user.role === 'admin' ? 'Admin' : 'Utente'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                        }`}>
                        {user.isActive ? 'Attivo' : 'Inattivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(user.createdAt).toLocaleDateString('it-IT')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openEditModal(user)}
                          disabled={actionLoading !== null}
                          className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                        >
                          Modifica
                        </button>
                        <button
                          onClick={() => openPasswordModal(user)}
                          disabled={actionLoading !== null}
                          className="text-yellow-600 hover:text-yellow-900 disabled:opacity-50"
                        >
                          Password
                        </button>
                        <button
                          onClick={() => handleToggleActive(user)}
                          disabled={actionLoading === `toggle-${user._id}`}
                          className={`${user.isActive
                            ? 'text-red-600 hover:text-red-900'
                            : 'text-green-600 hover:text-green-900'
                            } disabled:opacity-50`}
                        >
                          {actionLoading === `toggle-${user._id}` ? (
                            <LoadingSpinner size="sm" />
                          ) : (
                            user.isActive ? 'Disattiva' : 'Attiva'
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user)}
                          disabled={actionLoading === `delete-${user._id}`}
                          className="text-red-600 hover:text-red-900 disabled:opacity-50"
                        >
                          {actionLoading === `delete-${user._id}` ? (
                            <LoadingSpinner size="sm" />
                          ) : (
                            'Elimina'
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            pagination={{
              currentPage: pagination.currentPage,
              totalPages: pagination.totalPages,
              totalItems: pagination.totalUsers,
              hasNext: pagination.hasNext,
              hasPrev: pagination.hasPrev
            }}
            onPageChange={(newPage) => setPagination(prev => ({ ...prev, currentPage: newPage }))}
            itemName="utenti"
            showItemCount={true}
          />
        </div>

        {/* Modal Creazione Utente */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Nuovo Utente</h3>
                <form onSubmit={handleCreateUser} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Username</label>
                    <input
                      type="text"
                      required
                      value={formData.username}
                      onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  {/*     <div>
                    <label className="block text-sm font-medium text-gray-700">Nome</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div> */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Password</label>
                    <input
                      type="password"
                      required
                      minLength={6}
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Ruolo</label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value as 'user' | 'admin' }))}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="user">Utente</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <button
                      type="submit"
                      disabled={actionLoading === 'create'}
                      className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading === 'create' ? <LoadingSpinner size="sm" /> : 'Crea Utente'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateModal(false);
                        setFormData(INITIAL_FORM_DATA);
                      }}
                      className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                    >
                      Annulla
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Modal Modifica Utente */}
        {showEditModal && selectedUser && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Modifica Utente</h3>
                <form onSubmit={handleEditUser} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Username</label>
                    <input
                      type="text"
                      value={editFormData.username || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, username: e.target.value }))}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  {/*     <div>
                    <label className="block text-sm font-medium text-gray-700">Nome</label>
                    <input
                      type="text"
                      value={editFormData.name || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      value={editFormData.email || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div> */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Ruolo</label>
                    <select
                      value={editFormData.role || 'user'}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, role: e.target.value as 'user' | 'admin' }))}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="user">Utente</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <button
                      type="submit"
                      disabled={actionLoading === 'edit'}
                      className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading === 'edit' ? <LoadingSpinner size="sm" /> : 'Salva Modifiche'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowEditModal(false);
                        setEditFormData({});
                        setSelectedUser(null);
                      }}
                      className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                    >
                      Annulla
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Modal Cambio Password */}
        {showPasswordModal && selectedUser && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Cambia Password - {selectedUser.name}
                </h3>
                <form onSubmit={handleUpdatePassword} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Nuova Password</label>
                    <input
                      type="password"
                      required
                      minLength={6}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Minimo 6 caratteri"
                    />
                  </div>
                  <div className="flex gap-3 pt-4">
                    <button
                      type="submit"
                      disabled={actionLoading === 'password'}
                      className="flex-1 bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading === 'password' ? <LoadingSpinner size="sm" /> : 'Aggiorna Password'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowPasswordModal(false);
                        setNewPassword('');
                        setSelectedUser(null);
                      }}
                      className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                    >
                      Annulla
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};